// /admin/sso — OIDC + SAML config UI (T122).
//
// Layout:
//   - Header with eyebrow and page title.
//   - Tab bar: "OIDC" | "SAML".
//   - Each tab shows a form for the respective IdP config plus an
//     attribute-mapping section and a "Test login" button.
//   - Save is per-tab (PATCH only sends the active provider's fields).
//
// The backend redacts client_secret and idp_metadata_xml to "**redacted**"
// so the UI shows a placeholder. Sending the placeholder back is a no-op
// (the backend ignores it), so editing other fields without touching the
// secret works correctly.

import { FormEvent, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import useSWR from 'swr';
import {
  Check,
  ExternalLink,
  KeyRound,
  Loader2,
  Lock,
  Plus,
  Settings,
  Shield,
  Trash2,
} from 'lucide-react';
import {
  ADMIN_SSO_KEY,
  ApiError,
  LDAPConfigPatch,
  OIDCConfigPatch,
  SAMLConfigPatch,
  SSOConfig,
  adminSSOApi,
  fetcher,
} from '../lib/api';
import { FeatureGate } from '../components/FeatureLock';
import { PageHeader, Table, TBody, TD, TH, THead, TR } from '../components/ui';

const REDACTED = '**redacted**';

// ---------------------------------------------------------------------------
// Page root
// ---------------------------------------------------------------------------

type Tab = 'oidc' | 'saml' | 'ldap';

export function AdminSSO() {
  const [activeTab, setActiveTab] = useState<Tab>('oidc');

  const { data, error, isLoading, mutate } =
    useSWR<SSOConfig>(ADMIN_SSO_KEY, fetcher, { revalidateOnFocus: false });

  return (
    <div className="max-w-3xl mx-auto p-8 w-full">
      <PageHeader
        eyebrow="orbital · admin"
        icon={<Lock />}
        title="Single Sign-On"
        description={
          <>
            Configure OIDC, SAML 2.0, and LDAP/Active Directory identity providers.
            Changes are stored in the database and take effect on the next login attempt.
          </>
        }
        actions={
          <Link
            to="/admin/settings"
            className="inline-flex items-center gap-1.5 text-xs text-fg-muted hover:text-fg transition-colors"
          >
            <Settings className="h-3.5 w-3.5" aria-hidden="true" />
            Settings
          </Link>
        }
      />

      <FeatureGate feature="sso" label="Single Sign-On">
        {/* Tab bar */}
        <div className="flex gap-1 mb-6 border-b border-border">
          {(['oidc', 'saml', 'ldap'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`
                px-4 py-2 text-xs font-semibold uppercase tracking-[0.08em]
                transition-colors border-b-2 -mb-px
                ${activeTab === tab
                  ? 'border-accent text-accent'
                  : 'border-transparent text-fg-muted hover:text-fg'}
              `}
            >
              {tab.toUpperCase()}
            </button>
          ))}
        </div>

        {(() => {
          if (isLoading) {
            return (
              <div className="orb-card p-8 flex items-center justify-center gap-3 text-fg-muted">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                <span className="text-sm">Loading SSO config…</span>
              </div>
            );
          } else if (error) {
            return <ErrorBanner error={error} />;
          } else if (data) {
            if (activeTab === 'oidc') {
              return <OIDCForm config={data} onSaved={() => mutate()} />;
            } else if (activeTab === 'saml') {
              return <SAMLForm config={data} onSaved={() => mutate()} />;
            } else {
              return <LDAPForm config={data} onSaved={() => mutate()} />;
            }
          } else {
            return null;
          }
        })()}
      </FeatureGate>
    </div>
  );
}

// ---------------------------------------------------------------------------
// OIDC form
// ---------------------------------------------------------------------------

interface OIDCFormProps {
  readonly config: SSOConfig;
  readonly onSaved: () => void;
}

function OIDCForm({ config, onSaved }: OIDCFormProps) {
  const [enabled, setEnabled] = useState(config.oidc.enabled);
  const [issuer, setIssuer] = useState(config.oidc.issuer);
  const [clientID, setClientID] = useState(config.oidc.client_id);
  const [clientSecret, setClientSecret] = useState(
    config.oidc.client_secret === REDACTED ? '' : config.oidc.client_secret,
  );
  const [secretSet, setSecretSet] = useState(config.oidc.client_secret === REDACTED);
  const [redirectURL, setRedirectURL] = useState(config.oidc.redirect_url);
  const [emailClaim, setEmailClaim] = useState(config.oidc.email_claim);
  const [nameClaim, setNameClaim] = useState(config.oidc.name_claim);
  const [roleClaim, setRoleClaim] = useState(config.oidc.role_claim);
  const [roleMap, setRoleMap] = useState<Record<string, string>>(
    config.oidc.role_map ?? {},
  );

  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Keep form in sync if the parent re-fetches (e.g., after a save).
  useEffect(() => {
    setEnabled(config.oidc.enabled);
    setIssuer(config.oidc.issuer);
    setClientID(config.oidc.client_id);
    setSecretSet(config.oidc.client_secret === REDACTED);
    setClientSecret(config.oidc.client_secret === REDACTED ? '' : config.oidc.client_secret);
    setRedirectURL(config.oidc.redirect_url);
    setEmailClaim(config.oidc.email_claim);
    setNameClaim(config.oidc.name_claim);
    setRoleClaim(config.oidc.role_claim);
    setRoleMap(config.oidc.role_map ?? {});
  }, [config]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setErr(null);
    const patch: OIDCConfigPatch = {
      enabled,
      issuer,
      client_id: clientID,
      redirect_url: redirectURL,
      email_claim: emailClaim,
      name_claim: nameClaim,
      role_claim: roleClaim,
      role_map: roleMap,
    };
    // Only send client_secret if the user typed a new value.
    if (clientSecret && clientSecret !== REDACTED) {
      patch.client_secret = clientSecret;
    }
    try {
      await adminSSOApi.patch({ oidc: patch });
      setSaved(true);
      setTimeout(() => setSaved(false), 1500);
      onSaved();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Enabled toggle */}
      <section className="orb-card p-5 space-y-4">
        <SectionTitle icon={<Shield className="h-4 w-4" />} title="Provider" />

        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            className="accent-accent h-4 w-4"
          />
          <span className="text-sm text-fg">Enable OIDC single sign-on</span>
        </label>

        <div className="space-y-3">
          <Field label="Issuer URL" htmlFor="oidc-issuer">
            <input
              id="oidc-issuer"
              type="url"
              value={issuer}
              onChange={(e) => setIssuer(e.target.value)}
              className={inputCls}
              placeholder="https://accounts.google.com"
            />
          </Field>
          <Field label="Client ID" htmlFor="oidc-client-id">
            <input
              id="oidc-client-id"
              type="text"
              value={clientID}
              onChange={(e) => setClientID(e.target.value)}
              className={inputCls}
              placeholder="my-app-client-id"
            />
          </Field>
          <Field label="Client secret" htmlFor="oidc-client-secret">
            <div className="relative">
              <input
                id="oidc-client-secret"
                type="password"
                value={clientSecret}
                onChange={(e) => { setClientSecret(e.target.value); setSecretSet(false); }}
                className={inputCls}
                placeholder={secretSet ? '(stored — leave blank to keep)' : 'Enter secret…'}
              />
              {secretSet ? (
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] text-success">
                  stored
                </span>
              ) : null}
            </div>
            <p className="text-[11px] text-fg-subtle mt-1">
              Leave blank to keep the existing stored secret.
            </p>
          </Field>
          <Field label="Redirect URL" htmlFor="oidc-redirect">
            <input
              id="oidc-redirect"
              type="url"
              value={redirectURL}
              onChange={(e) => setRedirectURL(e.target.value)}
              className={inputCls}
              placeholder="https://lint.example.com/auth/oidc/callback"
            />
          </Field>
        </div>
      </section>

      {/* Attribute mapping */}
      <section className="orb-card p-5 space-y-4">
        <SectionTitle icon={<KeyRound className="h-4 w-4" />} title="Attribute mapping" />
        <p className="text-xs text-fg-muted">
          Map OIDC token claims to orbital-lint user fields.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Field label="Email claim" htmlFor="oidc-email-claim">
            <input
              id="oidc-email-claim"
              type="text"
              value={emailClaim}
              onChange={(e) => setEmailClaim(e.target.value)}
              className={inputCls}
              placeholder="email"
            />
          </Field>
          <Field label="Name claim" htmlFor="oidc-name-claim">
            <input
              id="oidc-name-claim"
              type="text"
              value={nameClaim}
              onChange={(e) => setNameClaim(e.target.value)}
              className={inputCls}
              placeholder="name"
            />
          </Field>
          <Field label="Role claim" htmlFor="oidc-role-claim">
            <input
              id="oidc-role-claim"
              type="text"
              value={roleClaim}
              onChange={(e) => setRoleClaim(e.target.value)}
              className={inputCls}
              placeholder="groups (optional)"
            />
          </Field>
        </div>

        <RoleMapEditor roleMap={roleMap} onChange={setRoleMap} />
      </section>

      {/* Actions */}
      {err ? <p role="alert" className="text-xs text-danger">{err}</p> : null}
      <div className="flex items-center gap-3">
        <button type="submit" disabled={saving || saved} className={primaryBtnCls}>
          {(() => {
            if (saved) {
              return <><Check className="h-3.5 w-3.5" aria-hidden="true" /> Saved</>;
            } else if (saving) {
              return <><Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> Saving…</>;
            } else {
              return 'Save OIDC settings';
            }
          })()}
        </button>
        {enabled ? (
          <a
            href="/auth/oidc/login"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 text-xs text-accent hover:opacity-80 transition-opacity"
          >
            <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
            Test login
          </a>
        ) : null}
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// SAML form
// ---------------------------------------------------------------------------

interface SAMLFormProps {
  readonly config: SSOConfig;
  readonly onSaved: () => void;
}

function SAMLForm({ config, onSaved }: SAMLFormProps) {
  const [enabled, setEnabled] = useState(config.saml.enabled);
  const [idpMetadataURL, setIdpMetadataURL] = useState(config.saml.idp_metadata_url);
  const [idpMetadataXML, setIdpMetadataXML] = useState(
    config.saml.idp_metadata_xml === REDACTED ? '' : config.saml.idp_metadata_xml,
  );
  const [xmlStored, setXmlStored] = useState(config.saml.idp_metadata_xml === REDACTED);
  const [acsURL, setAcsURL] = useState(config.saml.acs_url);
  const [emailAttr, setEmailAttr] = useState(config.saml.email_attr);
  const [nameAttr, setNameAttr] = useState(config.saml.name_attr);
  const [roleAttr, setRoleAttr] = useState(config.saml.role_attr);
  const [roleMap, setRoleMap] = useState<Record<string, string>>(
    config.saml.role_map ?? {},
  );
  const [allowIDPInitiated, setAllowIDPInitiated] = useState(config.saml.allow_idp_initiated);

  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    setEnabled(config.saml.enabled);
    setIdpMetadataURL(config.saml.idp_metadata_url);
    setXmlStored(config.saml.idp_metadata_xml === REDACTED);
    setIdpMetadataXML(config.saml.idp_metadata_xml === REDACTED ? '' : config.saml.idp_metadata_xml);
    setAcsURL(config.saml.acs_url);
    setEmailAttr(config.saml.email_attr);
    setNameAttr(config.saml.name_attr);
    setRoleAttr(config.saml.role_attr);
    setRoleMap(config.saml.role_map ?? {});
    setAllowIDPInitiated(config.saml.allow_idp_initiated);
  }, [config]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setErr(null);
    const patch: SAMLConfigPatch = {
      enabled,
      idp_metadata_url: idpMetadataURL,
      acs_url: acsURL,
      email_attr: emailAttr,
      name_attr: nameAttr,
      role_attr: roleAttr,
      role_map: roleMap,
      allow_idp_initiated: allowIDPInitiated,
    };
    if (idpMetadataXML && idpMetadataXML !== REDACTED) {
      patch.idp_metadata_xml = idpMetadataXML;
    }
    try {
      await adminSSOApi.patch({ saml: patch });
      setSaved(true);
      setTimeout(() => setSaved(false), 1500);
      onSaved();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <section className="orb-card p-5 space-y-4">
        <SectionTitle icon={<Shield className="h-4 w-4" />} title="Identity Provider" />

        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            className="accent-accent h-4 w-4"
          />
          <span className="text-sm text-fg">Enable SAML 2.0 single sign-on</span>
        </label>

        <div className="space-y-3">
          <Field label="IdP metadata URL" htmlFor="saml-metadata-url">
            <input
              id="saml-metadata-url"
              type="url"
              value={idpMetadataURL}
              onChange={(e) => setIdpMetadataURL(e.target.value)}
              className={inputCls}
              placeholder="https://idp.example.com/metadata.xml"
            />
            <p className="text-[11px] text-fg-subtle mt-1">
              Takes priority over inline XML when both are set.
            </p>
          </Field>

          <Field label="IdP metadata XML" htmlFor="saml-metadata-xml">
            <textarea
              id="saml-metadata-xml"
              value={idpMetadataXML}
              onChange={(e) => { setIdpMetadataXML(e.target.value); setXmlStored(false); }}
              rows={4}
              className={`${inputCls} font-mono text-xs resize-y`}
              placeholder={xmlStored ? '(stored — leave blank to keep)' : 'Paste XML here…'}
            />
            {xmlStored ? (
              <p className="text-[11px] text-success mt-1">Stored — leave blank to keep.</p>
            ) : null}
          </Field>

          <Field label="ACS URL" htmlFor="saml-acs-url">
            <input
              id="saml-acs-url"
              type="url"
              value={acsURL}
              onChange={(e) => setAcsURL(e.target.value)}
              className={inputCls}
              placeholder="https://lint.example.com/auth/saml/acs"
            />
          </Field>

          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={allowIDPInitiated}
              onChange={(e) => setAllowIDPInitiated(e.target.checked)}
              className="accent-accent h-4 w-4"
            />
            <span className="text-sm text-fg">Allow IdP-initiated login</span>
          </label>
        </div>
      </section>

      <section className="orb-card p-5 space-y-4">
        <SectionTitle icon={<KeyRound className="h-4 w-4" />} title="Attribute mapping" />
        <p className="text-xs text-fg-muted">
          Map SAML assertion attributes to orbital-lint user fields.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Field label="Email attribute" htmlFor="saml-email-attr">
            <input
              id="saml-email-attr"
              type="text"
              value={emailAttr}
              onChange={(e) => setEmailAttr(e.target.value)}
              className={inputCls}
              placeholder="email"
            />
          </Field>
          <Field label="Name attribute" htmlFor="saml-name-attr">
            <input
              id="saml-name-attr"
              type="text"
              value={nameAttr}
              onChange={(e) => setNameAttr(e.target.value)}
              className={inputCls}
              placeholder="displayName"
            />
          </Field>
          <Field label="Role attribute" htmlFor="saml-role-attr">
            <input
              id="saml-role-attr"
              type="text"
              value={roleAttr}
              onChange={(e) => setRoleAttr(e.target.value)}
              className={inputCls}
              placeholder="memberOf (optional)"
            />
          </Field>
        </div>

        <RoleMapEditor roleMap={roleMap} onChange={setRoleMap} />
      </section>

      {err ? <p role="alert" className="text-xs text-danger">{err}</p> : null}
      <div className="flex items-center gap-3">
        <button type="submit" disabled={saving || saved} className={primaryBtnCls}>
          {(() => {
            if (saved) {
              return <><Check className="h-3.5 w-3.5" aria-hidden="true" /> Saved</>;
            } else if (saving) {
              return <><Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> Saving…</>;
            } else {
              return 'Save SAML settings';
            }
          })()}
        </button>
        {enabled ? (
          <a
            href="/auth/saml/login"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 text-xs text-accent hover:opacity-80 transition-opacity"
          >
            <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
            Test login
          </a>
        ) : null}
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// LDAP / Active Directory form
// ---------------------------------------------------------------------------

interface LDAPFormProps {
  readonly config: SSOConfig;
  readonly onSaved: () => void;
}

function LDAPForm({ config, onSaved }: LDAPFormProps) {
  const ldap = config.ldap ?? {
    enabled: false, url: '', bind_dn: '', bind_password: '',
    user_search_base: '', user_search_filter: '(uid=%s)',
    email_attr: 'mail', name_attr: 'cn', role_attr: '',
    role_map: {}, use_tls: true, skip_cert_verify: false,
  };

  const [enabled, setEnabled] = useState(ldap.enabled);
  const [url, setUrl] = useState(ldap.url);
  const [bindDN, setBindDN] = useState(ldap.bind_dn);
  const [bindPassword, setBindPassword] = useState(
    ldap.bind_password === REDACTED ? '' : ldap.bind_password,
  );
  const [passwordSet, setPasswordSet] = useState(ldap.bind_password === REDACTED);
  const [searchBase, setSearchBase] = useState(ldap.user_search_base);
  const [searchFilter, setSearchFilter] = useState(ldap.user_search_filter);
  const [emailAttr, setEmailAttr] = useState(ldap.email_attr);
  const [nameAttr, setNameAttr] = useState(ldap.name_attr);
  const [roleAttr, setRoleAttr] = useState(ldap.role_attr);
  const [roleMap, setRoleMap] = useState<Record<string, string>>(ldap.role_map ?? {});
  const [useTLS, setUseTLS] = useState(ldap.use_tls);

  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    const l = config.ldap ?? ldap;
    setEnabled(l.enabled);
    setUrl(l.url);
    setBindDN(l.bind_dn);
    setPasswordSet(l.bind_password === REDACTED);
    setBindPassword(l.bind_password === REDACTED ? '' : l.bind_password);
    setSearchBase(l.user_search_base);
    setSearchFilter(l.user_search_filter);
    setEmailAttr(l.email_attr);
    setNameAttr(l.name_attr);
    setRoleAttr(l.role_attr);
    setRoleMap(l.role_map ?? {});
    setUseTLS(l.use_tls);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setErr(null);
    const patch: LDAPConfigPatch = {
      enabled, url, bind_dn: bindDN,
      user_search_base: searchBase,
      user_search_filter: searchFilter,
      email_attr: emailAttr, name_attr: nameAttr, role_attr: roleAttr,
      role_map: roleMap, use_tls: useTLS, skip_cert_verify: false,
    };
    if (bindPassword && bindPassword !== REDACTED) {
      patch.bind_password = bindPassword;
    }
    try {
      await adminSSOApi.patch({ ldap: patch });
      setSaved(true);
      setTimeout(() => setSaved(false), 1500);
      onSaved();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Connection */}
      <section className="orb-card p-5 space-y-4">
        <SectionTitle icon={<Shield className="h-4 w-4" />} title="Directory connection" />

        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            className="accent-accent h-4 w-4"
          />
          <span className="text-sm text-fg">Enable LDAP / Active Directory sign-on</span>
        </label>

        <div className="space-y-3">
          <Field label="Server URL" htmlFor="ldap-url">
            <input
              id="ldap-url"
              type="text"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              className={inputCls}
              placeholder="ldaps://ldap.corp.example.com:636"
            />
            <p className="text-[11px] text-fg-subtle mt-1">
              Use <code>ldaps://</code> for LDAPS or <code>ldap://</code> with STARTTLS enabled below.
            </p>
          </Field>

          <Field label="Bind DN (service account)" htmlFor="ldap-bind-dn">
            <input
              id="ldap-bind-dn"
              type="text"
              value={bindDN}
              onChange={(e) => setBindDN(e.target.value)}
              className={inputCls}
              placeholder="cn=svc-orbital,ou=service-accounts,dc=corp,dc=example,dc=com"
            />
          </Field>

          <Field label="Bind password" htmlFor="ldap-bind-password">
            <div className="relative">
              <input
                id="ldap-bind-password"
                type="password"
                value={bindPassword}
                onChange={(e) => { setBindPassword(e.target.value); setPasswordSet(false); }}
                className={inputCls}
                placeholder={passwordSet ? '(stored — leave blank to keep)' : 'Enter password…'}
              />
              {passwordSet ? (
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] text-success">
                  stored
                </span>
              ) : null}
            </div>
          </Field>

          <Field label="User search base" htmlFor="ldap-search-base">
            <input
              id="ldap-search-base"
              type="text"
              value={searchBase}
              onChange={(e) => setSearchBase(e.target.value)}
              className={inputCls}
              placeholder="ou=users,dc=corp,dc=example,dc=com"
            />
          </Field>

          <Field label="User search filter" htmlFor="ldap-search-filter">
            <input
              id="ldap-search-filter"
              type="text"
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              className={inputCls}
              placeholder="(uid=%s)"
            />
            <p className="text-[11px] text-fg-subtle mt-1">
              <code>%s</code> is replaced with the escaped username.
              Active Directory: <code>(sAMAccountName=%s)</code>
            </p>
          </Field>

          <div className="flex flex-col gap-2">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={useTLS}
                onChange={(e) => setUseTLS(e.target.checked)}
                className="accent-accent h-4 w-4"
              />
              <span className="text-sm text-fg">Use STARTTLS (for plain <code>ldap://</code>)</span>
            </label>
          </div>
        </div>
      </section>

      {/* Attribute mapping */}
      <section className="orb-card p-5 space-y-4">
        <SectionTitle icon={<KeyRound className="h-4 w-4" />} title="Attribute mapping" />
        <p className="text-xs text-fg-muted">
          Map LDAP entry attributes to orbital-lint user fields. Defaults work for
          most OpenLDAP deployments; Active Directory typically uses <code>mail</code> and{' '}
          <code>displayName</code>.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Field label="Email attribute" htmlFor="ldap-email-attr">
            <input
              id="ldap-email-attr"
              type="text"
              value={emailAttr}
              onChange={(e) => setEmailAttr(e.target.value)}
              className={inputCls}
              placeholder="mail"
            />
          </Field>
          <Field label="Display name attribute" htmlFor="ldap-name-attr">
            <input
              id="ldap-name-attr"
              type="text"
              value={nameAttr}
              onChange={(e) => setNameAttr(e.target.value)}
              className={inputCls}
              placeholder="cn"
            />
          </Field>
          <Field label="Role attribute (optional)" htmlFor="ldap-role-attr">
            <input
              id="ldap-role-attr"
              type="text"
              value={roleAttr}
              onChange={(e) => setRoleAttr(e.target.value)}
              className={inputCls}
              placeholder="memberOf"
            />
          </Field>
        </div>

        <RoleMapEditor roleMap={roleMap} onChange={setRoleMap} />
      </section>

      {err ? <p role="alert" className="text-xs text-danger">{err}</p> : null}
      <div className="flex items-center gap-3">
        <button type="submit" disabled={saving || saved} className={primaryBtnCls}>
          {(() => {
            if (saved) {
              return <><Check className="h-3.5 w-3.5" aria-hidden="true" /> Saved</>;
            } else if (saving) {
              return <><Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> Saving…</>;
            } else {
              return 'Save LDAP settings';
            }
          })()}
        </button>
        {enabled ? (
          <a
            href="/auth/ldap/login"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 text-xs text-accent hover:opacity-80 transition-opacity"
          >
            <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
            Test login
          </a>
        ) : null}
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Role map editor — add / remove IdP role → orbital-lint role entries
// ---------------------------------------------------------------------------

interface RoleMapEditorProps {
  readonly roleMap: Record<string, string>;
  readonly onChange: (map: Record<string, string>) => void;
}

const ORBITAL_ROLES = [
  'reader',
  'developer',
  'maintainer',
  'project_admin',
  'org_admin',
] as const;

function RoleMapEditor({ roleMap, onChange }: RoleMapEditorProps) {
  const [newKey, setNewKey] = useState('');
  const [newVal, setNewVal] = useState<string>(ORBITAL_ROLES[0]);

  const entries = Object.entries(roleMap);

  const handleAdd = () => {
    const key = newKey.trim();
    if (!key) return;
    onChange({ ...roleMap, [key]: newVal });
    setNewKey('');
    setNewVal(ORBITAL_ROLES[0]);
  };

  const handleRemove = (key: string) => {
    const updated = { ...roleMap };
    delete updated[key];
    onChange(updated);
  };

  return (
    <div className="space-y-2">
      <p className="orb-section-title text-[11px] text-fg-subtle">Role mapping</p>
      <p className="text-[11px] text-fg-muted">
        Translate IdP role values to orbital-lint roles. Users without a match
        get the default "developer" role on first login.
      </p>

      {entries.length > 0 ? (
        <Table bare dense className="mb-2">
          <THead>
            <TR>
              <TH>IdP role value</TH>
              <TH>orbital-lint role</TH>
              <TH srOnly>Actions</TH>
            </TR>
          </THead>
          <TBody>
            {entries.map(([k, v]) => (
              <TR key={k}>
                <TD mono className="text-accent">{k}</TD>
                <TD>
                  <select
                    value={v}
                    onChange={(e) => onChange({ ...roleMap, [k]: e.target.value })}
                    className={`${inputCls} text-xs py-0.5`}
                  >
                    {ORBITAL_ROLES.map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                </TD>
                <TD>
                  <button
                    type="button"
                    onClick={() => handleRemove(k)}
                    className="p-1 rounded hover:bg-bg-subtle text-fg-muted hover:text-danger transition-colors opacity-0 group-hover:opacity-100"
                    title={`Remove mapping for "${k}"`}
                  >
                    <Trash2 className="h-3 w-3" aria-hidden="true" />
                  </button>
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      ) : (
        <p className="text-xs text-fg-subtle italic">No role mappings configured.</p>
      )}

      {/* Add row */}
      <div className="flex items-end gap-2 flex-wrap">
        <div className="flex flex-col gap-1">
          <label htmlFor="sso-role-key" className="text-[11px] text-fg-subtle">IdP role value</label>
          <input
            id="sso-role-key"
            type="text"
            value={newKey}
            onChange={(e) => setNewKey(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAdd(); } }}
            className={`${inputCls} text-xs py-1`}
            placeholder="admin"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor="sso-role-val" className="text-[11px] text-fg-subtle">orbital-lint role</label>
          <select
            id="sso-role-val"
            value={newVal}
            onChange={(e) => setNewVal(e.target.value)}
            className={`${inputCls} text-xs py-1`}
          >
            {ORBITAL_ROLES.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
        </div>
        <button
          type="button"
          onClick={handleAdd}
          disabled={!newKey.trim()}
          className={`${primaryBtnCls} self-end`}
        >
          <Plus className="h-3.5 w-3.5" aria-hidden="true" />
          Add
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared sub-components
// ---------------------------------------------------------------------------

function SectionTitle({
  icon,
  title,
}: {
  readonly icon: React.ReactNode;
  readonly title: string;
}) {
  return (
    <div className="flex items-center gap-2 text-sm font-semibold text-fg">
      <span className="text-accent">{icon}</span>
      {title}
    </div>
  );
}

function Field({
  label,
  htmlFor,
  children,
}: {
  readonly label: string;
  readonly htmlFor: string;
  readonly children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={htmlFor} className="orb-section-title text-[11px]">{label}</label>
      {children}
    </div>
  );
}

function ErrorBanner({ error }: { readonly error: unknown }) {
  const msg = formatApiError(error);
  const hint =
    error instanceof ApiError && error.code === 'insufficient_role'
      ? 'Only org admins can manage SSO configuration.'
      : null;
  return (
    <div role="alert" className="orb-card p-6 border-danger/40 bg-danger/5">
      <p className="orb-eyebrow text-danger mb-2">request failed</p>
      <p className="text-sm text-fg">{msg}</p>
      {hint ? <p className="text-xs text-fg-muted mt-2">{hint}</p> : null}
    </div>
  );
}

function formatApiError(e: unknown): string {
  if (e instanceof ApiError) {
    return `${e.code}: ${e.message}`;
  } else if (e instanceof Error) {
    return e.message;
  } else {
    return 'request failed';
  }
}

// ---------------------------------------------------------------------------
// Shared styles
// ---------------------------------------------------------------------------

const inputCls = `
  w-full px-3 py-2 rounded-md border border-border
  bg-bg-inset text-fg text-sm placeholder-fg-subtle
  focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent/50
  transition-colors
`.trim();

const primaryBtnCls = `
  inline-flex items-center gap-1.5 px-4 py-2 rounded-md
  text-xs font-semibold uppercase tracking-[0.08em]
  bg-accent text-accent-fg hover:opacity-90
  border border-accent/40 transition-opacity
  disabled:opacity-30 disabled:cursor-not-allowed
`.trim();
